import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { MemberDirectoryRow, PaginatedResult, SupabaseClientLike } from './people';

export type OrgNodeStatus =
  | 'active'
  | 'invited'
  | 'suspended'
  | 'terminated'
  | 'inactive'
  | 'on_leave';

type OrgProfileRow = {
  user_id: string;
  manager_id: string | null;
  title: string | null;
  department: string | null;
  status: string | null;
};

export interface OrgNode {
  id: string;
  name: string;
  title: string;
  department: string;
  avatar_url: string | null;
  status: OrgNodeStatus;
  direct_reports: OrgNode[];
  manager_id: string | null;
  manager_name: string | null;
}

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(supabase: unknown): SupabaseAuthLike {
  if (supabase) return supabase as SupabaseAuthLike;
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return { auth: { getSession: async () => ({ data: { session: null } }) } };
  }
}

async function getAuthHeaders(supabase: unknown): Promise<Record<string, string>> {
  const client = resolveClient(supabase);
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function fetchDirectoryMembers(supabase: unknown): Promise<MemberDirectoryRow[]> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch('/functions/v1/people/members?limit=500', {
    headers: { 'content-type': 'application/json', ...headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }

  const payload = (await res.json()) as PaginatedResult<MemberDirectoryRow>;
  return payload.data ?? [];
}

function normalizeStatus(status: string | null | undefined): OrgNodeStatus {
  if (
    status === 'active' ||
    status === 'invited' ||
    status === 'suspended' ||
    status === 'terminated' ||
    status === 'on_leave' ||
    status === 'inactive'
  ) {
    return status;
  }
  return 'active';
}

function buildOrgTree(profiles: OrgProfileRow[], directoryRows: MemberDirectoryRow[]): OrgNode[] {
  const directoryById = new Map(directoryRows.map((row) => [row.user_id, row]));
  const nodes = new Map<string, OrgNode>();

  for (const profile of profiles) {
    const directory = directoryById.get(profile.user_id);
    nodes.set(profile.user_id, {
      id: profile.user_id,
      name: directory?.display_name ?? directory?.email ?? profile.user_id,
      title: profile.title ?? directory?.title ?? '',
      department: profile.department ?? directory?.department ?? '',
      avatar_url: directory?.avatar_url ?? null,
      status: normalizeStatus(profile.status ?? directory?.status ?? 'active'),
      direct_reports: [],
      manager_id: profile.manager_id,
      manager_name: null,
    });
  }

  const roots: OrgNode[] = [];
  for (const node of nodes.values()) {
    const manager = node.manager_id ? nodes.get(node.manager_id) : null;
    node.manager_name = manager?.name ?? null;
    if (!manager) {
      roots.push(node);
      continue;
    }
    manager.direct_reports.push(node);
  }

  const sortNodes = (input: OrgNode[]) => {
    input.sort((left, right) => left.name.localeCompare(right.name));
    for (const node of input) sortNodes(node.direct_reports);
  };

  sortNodes(roots);
  return roots;
}

function matchesNode(node: OrgNode, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;
  return [node.name, node.title, node.department, node.manager_name ?? '']
    .join(' ')
    .toLowerCase()
    .includes(normalizedQuery);
}

function filterTree(
  nodes: OrgNode[],
  normalizedQuery: string,
  departmentSet: Set<string>,
): OrgNode[] {
  const result: OrgNode[] = [];

  for (const node of nodes) {
    const filteredChildren = filterTree(node.direct_reports, normalizedQuery, departmentSet);
    const matchesDepartment =
      departmentSet.size === 0 ||
      (node.department && departmentSet.has(node.department.toLowerCase()));
    const matchesSearch = matchesNode(node, normalizedQuery);

    if ((matchesDepartment && matchesSearch) || filteredChildren.length > 0) {
      result.push({
        ...node,
        direct_reports: filteredChildren,
      });
    }
  }

  return result;
}

export function useOrganizationChart(
  supabase: SupabaseClientLike,
  tenantId: string,
): UseQueryResult<OrgNode[]> {
  return useQuery({
    queryKey: ['organization-chart', tenantId],
    queryFn: async () => {
      const profilesClient = supabase as unknown as {
        from: (table: string) => {
          select: (columns: string) => {
            eq: (
              column: string,
              value: string,
            ) => Promise<{ data: OrgProfileRow[] | null; error: { message: string } | null }>;
          };
        };
      };

      const [{ data: profiles, error }, directoryRows] = await Promise.all([
        profilesClient
          .from('memberships_profile')
          .select('user_id, manager_id, title, department, status')
          .eq('tenant_id', tenantId),
        fetchDirectoryMembers(supabase),
      ]);

      if (error) throw new Error(error.message);
      return buildOrgTree(profiles ?? [], directoryRows);
    },
    enabled: Boolean(tenantId),
  });
}

export function useOrganizationChartSearch(
  supabase: SupabaseClientLike,
  tenantId: string,
  query: string,
  departments: string[] = [],
): UseQueryResult<OrgNode[]> {
  const chartQuery = useOrganizationChart(supabase, tenantId);
  const normalizedQuery = query.trim().toLowerCase();
  const departmentSet = useMemo(
    () => new Set(departments.map((department) => department.trim().toLowerCase()).filter(Boolean)),
    [departments],
  );

  const filteredData = useMemo(
    () => filterTree(chartQuery.data ?? [], normalizedQuery, departmentSet),
    [chartQuery.data, departmentSet, normalizedQuery],
  );

  return {
    ...chartQuery,
    data: filteredData,
  } as UseQueryResult<OrgNode[]>;
}
